// Hooks TanStack Query da Conferência de recebimento de NF-e — espelha
// frontend/src/hooks/useXmlTerceiros.ts (queryKeys, useQuery/useMutation, invalidação
// lista/detalhe após mutação). Diferença: aqui o backend não envelopa em {success,data}
// (ver api/conferencia.ts), então `select` lê `response.data` direto.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isAxiosError, type AxiosResponse } from 'axios';
import { conferenciaApi } from '../api/conferencia';
import { conferenciaPendenciaApi } from '../api/conferenciaPendencia';
import type {
  AtualizarCabecalhoConferenciaRequest,
  AtualizarItemConferenciaRequest,
  ConcluirConferenciaRequest,
  ConferenciaNfe,
  IniciarConferenciaRequest,
  ListarConferenciasFiltros,
} from '../types/conferencia';
import { selecionarObjectUrlsParaRevogar } from '../utils/cacheObjectUrl';
import { logError } from '../utils/clientLog';
import { aplicarLimiteConferencia, statusItemOtimista } from '../utils/statusConferencia';

// Keys para cache do React Query
export const conferenciaKeys = {
  all: ['conferencias'] as const,
  lists: () => [...conferenciaKeys.all, 'list'] as const,
  list: (filtros: ListarConferenciasFiltros = {}) => [...conferenciaKeys.lists(), filtros] as const,
  details: () => [...conferenciaKeys.all, 'detail'] as const,
  detail: (id: number) => [...conferenciaKeys.details(), id] as const,
  fotos: (id: number) => [...conferenciaKeys.all, 'fotos', id] as const,
  imagemProduto: (codigo: string | null | undefined) =>
    [...conferenciaKeys.all, 'imagem-produto', codigo] as const,
  ocorrencias: (id: number) => [...conferenciaKeys.all, 'ocorrencias', id] as const,
  pendencias: (filtros: ListarConferenciasFiltros = {}) =>
    [...conferenciaKeys.all, 'pendencias', filtros] as const,
};

// ── Leitura ────────────────────────────────────────────────────────────

/** Lista cabeçalhos + resumo de divergência, com filtros opcionais. */
export function useConferencias(filtros: ListarConferenciasFiltros = {}) {
  return useQuery({
    queryKey: conferenciaKeys.list(filtros),
    queryFn: () => conferenciaApi.listar(filtros),
    select: (response) => response.data,
    staleTime: 1000 * 30,
  });
}

/** Detalhe (cabeçalho + itens) de uma conferência. */
export function useConferencia(id: number | undefined) {
  return useQuery({
    queryKey: id ? conferenciaKeys.detail(id) : [...conferenciaKeys.details(), 'disabled'],
    queryFn: () => conferenciaApi.obter(id!),
    select: (response) => response.data,
    enabled: !!id,
    staleTime: 1000 * 10,
  });
}

/**
 * Metadados das fotos anexadas à conferência. `api` seleciona a fonte (Task 8 —
 * GaleriaFotosConferencia): `'nfe'` (endpoints `/conferencia-nfe/...`, padrão — workspace) ou
 * `'pendencia'` (endpoints `/conferencia-pendencia/...`, dialog de pendência — supervisor
 * só-`conferencia_pendencia` não tem a policy `conferencia_nfe` e tomaria 403 no GET direto).
 * Mesma queryKey pras duas fontes — é a MESMA conferência por baixo, só a rota de leitura muda;
 * cache compartilhado entre workspace e dialog de pendência é desejado (evita fetch duplicado se
 * as duas telas olharem a mesma conferência na mesma sessão).
 */
export function useFotosConferencia(id: number | undefined, api: 'nfe' | 'pendencia' = 'nfe') {
  const client = api === 'pendencia' ? conferenciaPendenciaApi : conferenciaApi;
  return useQuery({
    queryKey: id ? conferenciaKeys.fotos(id) : [...conferenciaKeys.all, 'fotos', 'disabled'],
    queryFn: () => client.listarFotos(id!),
    select: (response) => response.data,
    enabled: !!id,
    staleTime: 1000 * 30,
  });
}

// ── Pendências (controller de união conferencia_nfe ∪ conferencia_pendencia) ──

/** Lista cabeçalhos filtrados por pendência — alimenta a tela de Pendências. */
export function usePendencias(filtros: ListarConferenciasFiltros = {}) {
  return useQuery({
    queryKey: conferenciaKeys.pendencias(filtros),
    queryFn: () => conferenciaPendenciaApi.listar(filtros),
    select: (response) => response.data,
    staleTime: 1000 * 30,
  });
}

/** Detalhe (cabeçalho + itens) de uma conferência, via controller de união. */
export function usePendenciaDetalhe(id: number | undefined) {
  return useQuery({
    queryKey: id ? conferenciaKeys.detail(id) : [...conferenciaKeys.details(), 'disabled'],
    queryFn: () => conferenciaPendenciaApi.obter(id!),
    select: (response) => response.data,
    enabled: !!id,
    staleTime: 1000 * 10,
  });
}

/** Linha do tempo (manual + sistema) da pendência, em ordem cronológica. Append-only. */
export function useOcorrencias(id: number | undefined) {
  return useQuery({
    queryKey: id ? conferenciaKeys.ocorrencias(id) : [...conferenciaKeys.all, 'ocorrencias', 'disabled'],
    queryFn: () => conferenciaPendenciaApi.listarOcorrencias(id!),
    select: (response) => response.data,
    enabled: !!id,
    staleTime: 1000 * 15,
  });
}

/** Adiciona uma entrada manual na linha do tempo — colaboração, não exige conferencia_pendencia. */
export function useRegistrarOcorrencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, texto }: { id: number; texto: string }) =>
      conferenciaPendenciaApi.registrarOcorrencia(id, { texto }),
    onSuccess: (_response, { id }) => {
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.ocorrencias(id) });
    },
    onError: (error: Error) => {
      logError('Erro ao registrar andamento:', error);
    },
  });
}

// ── Ciclo de vida ──────────────────────────────────────────────────────

/** Inicia (ou recupera, se já existir) a conferência de uma NF-e. */
export function useIniciarConferencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dados: IniciarConferenciaRequest) => conferenciaApi.iniciar(dados),
    onSuccess: (response) => {
      queryClient.setQueryData(conferenciaKeys.detail(response.data.id), response);
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
    },
    onError: (error: Error) => {
      logError('Erro ao iniciar conferência:', error);
    },
  });
}

export interface AtualizarCabecalhoVariables extends AtualizarCabecalhoConferenciaRequest {
  id: number;
}

/**
 * Edita setor de recebimento e observação do cabeçalho. Usa `invalidateQueries` (não
 * `setQueryData` como iniciar/concluir/reabrir) de propósito: a resposta do backend não
 * traz `contagemManualPermitida` recalculado com garantia (é `[NotMapped]`, default `false`
 * se algum caminho esquecer de preenchê-lo) — invalidar força um novo `ObterAsync`, que
 * sempre popula o campo, em vez de arriscar sobrescrever o cache com o valor default.
 */
export function useAtualizarCabecalho() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, setorRecebimento, observacao }: AtualizarCabecalhoVariables) =>
      conferenciaApi.atualizarCabecalho(id, { setorRecebimento, observacao }),
    onSuccess: (_response, { id }) => {
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
    },
    onError: (error: Error) => {
      logError('Erro ao atualizar cabeçalho da conferência:', error);
    },
  });
}

export function useConcluir() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body?: ConcluirConferenciaRequest }) =>
      conferenciaApi.concluir(id, body),
    onSuccess: (response, { id }) => {
      queryClient.setQueryData(conferenciaKeys.detail(id), response);
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
    },
    onError: (error: Error) => {
      logError('Erro ao concluir conferência:', error);
    },
  });
}

/**
 * Marca a pendência de uma conferência concluída como resolvida. Aponta pro controller de
 * união (`/conferencia-pendencia/{id}/resolver`) — a Task 6 removeu o endpoint antigo
 * (`/conferencia-nfe/{id}/pendencia/resolver`) do backend.
 */
export function useResolverPendencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { observacao?: string } }) =>
      conferenciaPendenciaApi.resolver(id, body.observacao),
    onSuccess: (response, { id }) => {
      queryClient.setQueryData(conferenciaKeys.detail(id), response);
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
      // A lista da tela de Pendências usa usePendencias (chave própria) — invalidar também.
      queryClient.invalidateQueries({ queryKey: [...conferenciaKeys.all, 'pendencias'] });
      // ResolverPendenciaAsync grava "Pendência resolvida" na linha do tempo — sem isto o
      // dialog aberto não mostra a entrada nova até reabrir (espelha useRegistrarOcorrencia).
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.ocorrencias(id) });
    },
    onError: (error: Error) => {
      logError('Erro ao resolver pendência:', error);
    },
  });
}

export function useReabrir() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => conferenciaApi.reabrir(id),
    onSuccess: (response, id) => {
      queryClient.setQueryData(conferenciaKeys.detail(id), response);
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
    },
    onError: (error: Error) => {
      logError('Erro ao reabrir conferência:', error);
    },
  });
}

export function useRemover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => conferenciaApi.remover(id),
    onSuccess: (_response, id) => {
      queryClient.removeQueries({ queryKey: conferenciaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
    },
    onError: (error: Error) => {
      logError('Erro ao remover conferência:', error);
    },
  });
}

// ── Bipagem ────────────────────────────────────────────────────────────

export interface AtualizarItemVariables {
  conferenciaId: number;
  itemId: number;
  body: AtualizarItemConferenciaRequest;
}

interface AtualizarItemContext {
  queryKey: ReturnType<typeof conferenciaKeys.detail>;
  anterior: AxiosResponse<ConferenciaNfe> | undefined;
}

/**
 * Bipagem/contagem de 1 item — chamada a cada leitura de código de barras. Precisa ser
 * instantânea (fila de bipagem não pode esperar round-trip), por isso faz patch otimista
 * do cache de useConferencia(conferenciaId) em onMutate e desfaz em onError; onSettled
 * sempre revalida contra o servidor (que recalcula o status com autoridade).
 */
export function useAtualizarItem() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<unknown>, Error, AtualizarItemVariables, AtualizarItemContext>({
    mutationFn: ({ conferenciaId, itemId, body }) =>
      conferenciaApi.atualizarItem(conferenciaId, itemId, body),

    onMutate: async ({ conferenciaId, itemId, body }) => {
      const queryKey = conferenciaKeys.detail(conferenciaId);
      await queryClient.cancelQueries({ queryKey });

      const anterior = queryClient.getQueryData<AxiosResponse<ConferenciaNfe>>(queryKey);

      if (anterior) {
        const proximo: ConferenciaNfe = {
          ...anterior.data,
          itens: anterior.data.itens.map((item) => {
            if (item.id !== itemId) return item;

            // Aplica o MESMO teto do servidor no patch, com a MESMA origem: bipe que estoura
            // é zerado (mostrar o número grande e vê-lo sumir no onSettled seria um piscar
            // confuso no erro); digitação confirmada acima da nota é mantida como excedente.
            const limite = aplicarLimiteConferencia(
              item.qtdNota, body.qtdConferida, body.qtdAvariada, body.permitirExcedente ?? false);

            return {
              ...item,
              qtdConferida: limite.qtdConferida,
              qtdAvariada: limite.qtdAvariada,
              observacao: body.observacao ?? null,
              status: statusItemOtimista(item.qtdNota, limite.qtdConferida, limite.qtdAvariada),
            };
          }),
        };
        queryClient.setQueryData(queryKey, { ...anterior, data: proximo });
      }

      return { queryKey, anterior };
    },

    onError: (error, _variables, context) => {
      logError('Erro ao bipar item da conferência:', error);
      if (context?.anterior) {
        queryClient.setQueryData(context.queryKey, context.anterior);
      }
    },

    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.detail(variables.conferenciaId) });
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.lists() });
    },
  });
}

// ── Fotos (anexo) ──────────────────────────────────────────────────────

/** `api` seleciona o client — ver comentário de {@link useFotosConferencia}. */
export function useEnviarFoto(api: 'nfe' | 'pendencia' = 'nfe') {
  const client = api === 'pendencia' ? conferenciaPendenciaApi : conferenciaApi;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conferenciaId, arquivo }: { conferenciaId: number; arquivo: File }) =>
      client.enviarFoto(conferenciaId, arquivo),
    onSuccess: (_response, { conferenciaId }) => {
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.fotos(conferenciaId) });
    },
    onError: (error: Error) => {
      logError('Erro ao enviar foto da conferência:', error);
    },
  });
}

/**
 * Sem parâmetro `api` — `ConferenciaPendenciaController` não expõe DELETE de foto (a policy de
 * união é leitura+colaboração; remover é ação do dono do fluxo `conferencia_nfe`). Quem chama com
 * `api='pendencia'` (GaleriaFotosConferencia) simplesmente não oferece o botão de remover.
 */
export function useRemoverFoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ anexoId }: { anexoId: number; conferenciaId: number }) =>
      conferenciaApi.removerFoto(anexoId),
    onSuccess: (_response, { conferenciaId }) => {
      queryClient.invalidateQueries({ queryKey: conferenciaKeys.fotos(conferenciaId) });
    },
    onError: (error: Error) => {
      logError('Erro ao remover foto da conferência:', error);
    },
  });
}

// ── Código de barras ───────────────────────────────────────────────────

/**
 * Resolve um EAN bipado pro produto interno. Mutation (não query) porque cada scan é um
 * lookup pontual sob demanda — não há um `ean` estável pra virar chave de cache reativa.
 */
export function useProdutoPorBarras() {
  return useMutation({
    mutationFn: (ean: string) => conferenciaApi.produtoPorBarras(ean),
  });
}

// ── Foto do produto ────────────────────────────────────────────────────

/**
 * Teto de object URLs de foto de produto vivas ao mesmo tempo. O backend entrega ~50-80 KB por
 * foto (resize pra 600px), então 120 ≈ 9 MB: folga de sobra pra maior nota da doca sem deixar
 * o terminal acumular o catálogo inteiro ao longo do turno.
 */
const MAX_URLS_IMAGEM_PRODUTO = 120;

/**
 * Object URLs por código, fora do React Query. O cache do Query guarda a STRING; o blob URL
 * em si precisa de um dono estável, senão cada remontagem do checklist criaria uma URL nova
 * pro mesmo produto.
 *
 * A ordem de inserção do Map é a ordem LRU — o acerto de cache reinsere a chave, então
 * `keys()` começa pela menos usada.
 */
const urlsImagemProduto = new Map<string, string>();

/**
 * Guarda a URL nova e devolve o Map ao teto.
 *
 * Revogar é obrigatório: `createObjectURL` prende o BLOB inteiro (não "alguns ponteiros") até
 * `revokeObjectURL` ou o reload da página, e o terminal da doca fica aberto o turno todo com
 * milhares de produtos distintos passando pela tela. Duas invariantes que a revogação não pode
 * quebrar, e por isso a escolha é LRU-com-guarda em vez de "revoga o mais antigo e pronto":
 *
 * - foto ainda montada nunca é revogada (`getObserversCount() > 0`) — a imagem viva ficaria
 *   apontando pra uma URL morta, sem nada que mandasse o React buscá-la de novo;
 * - a string revogada sai junto do cache do Query (`removeQueries`), senão uma remontagem
 *   futura receberia a URL morta de volta em vez de rebaixar o blob.
 */
function registrarUrlImagemProduto(queryClient: QueryClient, codigo: string, url: string) {
  urlsImagemProduto.set(codigo, url);

  const emUso = (chave: string) =>
    chave === codigo ||
    (queryClient
      .getQueryCache()
      .find({ queryKey: conferenciaKeys.imagemProduto(chave) })
      ?.getObserversCount() ?? 0) > 0;

  const revogar = selecionarObjectUrlsParaRevogar(
    [...urlsImagemProduto.keys()],
    MAX_URLS_IMAGEM_PRODUTO,
    emUso
  );

  for (const chave of revogar) {
    URL.revokeObjectURL(urlsImagemProduto.get(chave)!);
    urlsImagemProduto.delete(chave);
    queryClient.removeQueries({ queryKey: conferenciaKeys.imagemProduto(chave) });
  }
}

/**
 * Foto do produto pro checklist. Blob autenticado (o endpoint está sob policy, um `src` cru
 * daria 401) convertido em object URL; o componente mostra placeholder enquanto não há `data`.
 */
export function useImagemProduto(codigo: string | null | undefined) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: conferenciaKeys.imagemProduto(codigo),
    queryFn: async () => {
      const cached = urlsImagemProduto.get(codigo!);
      if (cached) {
        // Reinsere pra virar a mais recente do teto LRU (Map itera por ordem de inserção).
        urlsImagemProduto.delete(codigo!);
        urlsImagemProduto.set(codigo!, cached);
        return cached;
      }

      const blob = await conferenciaApi.baixarImagemProduto(codigo!);
      const url = URL.createObjectURL(blob);
      registrarUrlImagemProduto(queryClient, codigo!, url);
      return url;
    },
    enabled: !!codigo,
    // 404 = produto sem foto cadastrada, o caso COMUM numa nota: repetir seria só ruído de
    // rede. Já 500/502/timeout é indisponibilidade temporária — sem distinguir, um backend
    // fora do ar se disfarçaria de "nota inteira sem foto" e o conferente perderia a
    // checagem visual sem saber que perdeu. 1 retry só: são N requisições por nota.
    retry: (tentativas, erro) =>
      isAxiosError(erro) && erro.response?.status === 404 ? false : tentativas < 2,
    staleTime: Infinity,
    // O dono do blob é o Map acima, não o cache do Query: se a entrada for coletada, o queryFn
    // devolve a MESMA URL em vez de criar outra. gcTime finito só evita segurar uma entrada
    // por produto visto no turno.
    gcTime: 1000 * 60 * 30,
  });
}
